/**
 * Run-agent-review triggers — the on-demand AI review buttons + their status.
 *
 * Backend: eastview-os frontend_documentation/run-agent-reviews.md. Per the
 * "Where to call" section, the frontend calls these on the **main app** (the
 * normal LOS API base URL); the main app receives the request and triggers the
 * agent workflow in the listener. So we use `losHttp` as-is — its request
 * interceptor attaches the `Authorization: Bearer` the endpoints' require_admin
 * gate reads, and its baseURL already points at the LOS/main app.
 *
 * NOTE: as of this writing the run + status endpoints may not be live yet. Until
 * they land the calls simply error (or 404) and the panel degrades gracefully.
 *
 * Run-gating is enforced by the backend and surfaced via getStatus():
 *   - Loan Opening: run ONCE per loan.
 *   - Post-UW QC / Unexecuted-package: re-run only when the loan changed since
 *     the last run.
 * A blocked run POST returns 200 { status:"skipped", reason, message } (NOT 202).
 * Passing { force: true } in a run body is the admin override — it bypasses the
 * already_run / no_changes gate (the gate default is unchanged).
 */
import { losHttp } from "../lib/losHttp";

const base = (dealId) => `/admin/loans/${dealId}/ai-reviews`;

// Shared axios config: a friendly console label for the losHttp logger.
const cfg = (logName) => ({ logName });

export const losAiReviewsApi = {
  /**
   * GET /admin/loans/{dealId}/ai-reviews/status — drives the Run buttons.
   * Returns per-agent gating:
   *   { loan_opening:       { lastRun, canRun, reason },
   *     unexecuted_package: { lastRun, canRun, reason },
   *     post_uw_qc:         { lastRun, canRun, reason } }
   * reason ∈ already_run | no_changes | never_run | changed.
   */
  getStatus: (dealId) =>
    losHttp
      .get(`${base(dealId)}/status`, cfg("AI: reviews status"))
      .then((r) => r.data),

  /**
   * Loan Opening review. The live main-app endpoint (agent_reviews.py) QUEUES
   * this to the listener like the others and returns 202 { runId, status:
   * "queued", message } — the field population + processing-note update + missing
   * docs happen in the listener run, surfaced by the loan_opening_review_complete
   * notification (NOT this response). If gated (already run) returns 200
   * { status:"skipped", reason, message }. (The published doc still describes an
   * inline summary; the live endpoint queues it.)
   *
   * We don't send `force` — the button respects the once-per-loan gate via the
   * status endpoint (canRun) rather than bypassing it.
   */
  runOpeningReview: (dealId, body = {}) =>
    losHttp
      .post(`${base(dealId)}/opening/run`, body, cfg("AI: Loan Opening review"))
      .then((r) => r.data),

  /**
   * Post-UW QC review — ASYNC (Dapr workflow). Returns the launch envelope
   * { runId, agent, mode, status:"started" } immediately; findings ride the
   * `post_uw_qc_review_complete` completion notification, not this response.
   * If gated (no changes since last run) returns 200 { status:"skipped", … }.
   * Run AFTER the unexecuted-package indexer so the terms tie-out has drafts.
   */
  runPostUwQc: (dealId, body = {}) =>
    losHttp
      .post(`${base(dealId)}/post-uw-qc/run`, body, cfg("AI: Post-UW QC"))
      .then((r) => r.data),

  /**
   * Unexecuted-package indexing — ASYNC (Dapr workflow). Splits the Unexecuted
   * Loan Package into child docs and dispatches note/hud extraction so the
   * drafted-terms extracts exist for the Post-UW QC tie-out. Returns the launch
   * envelope; results ride `unexecuted_package_index_complete`. Idempotent.
   * `docOid` (optional) indexes a specific package version.
   */
  runUnexecutedPackageIndex: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/unexecuted-package/run`,
        body,
        cfg("AI: Unexecuted-package index"),
      )
      .then((r) => r.data),

  /**
   * Executed-package split — ASYNC. Day-of-funding release review: splits the
   * EXECUTED (signed) closing-package scan into per-document children (signature
   * blocks included) and dispatches their extracts, so the executed terms are on
   * file for the funding decision. The executed counterpart to Unexecuted-package
   * indexing. Same queued/skipped shape; fires `executedPackageIndexedRun`.
   */
  runExecutedPackage: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/executed-package/run`,
        body,
        cfg("AI: Executed-package split"),
      )
      .then((r) => r.data),

  /**
   * Hard exception check — ASYNC. Credit-facility DB + CL covenant gate (RTL /
   * Ground Up only; DSCR completes with applicable:false). Stages a
   * candidate_finding per check needing attention. Same queued/skipped shape;
   * fires `hard_exception_check_complete`.
   */
  runHardExceptions: (dealId, body = {}) =>
    losHttp
      .post(`${base(dealId)}/hard-exceptions/run`, body, cfg("AI: Hard exceptions"))
      .then((r) => r.data),

  /**
   * Generate suggested conditions — ASYNC. Runs the finding emitters
   * (missing-doc + per-extract/cross-doc specialists), staging candidate_findings
   * for review. Same queued/skipped shape; fires `suggested_conditions_complete`.
   */
  runSuggestConditions: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/suggest-conditions/run`,
        body,
        cfg("AI: Suggest conditions"),
      )
      .then((r) => r.data),

  /**
   * Field-difference review — ASYNC. Compares extracted document values against
   * the entered loan values (sizer economics, appraisal, FICO, entity legal name)
   * and stages a finding per material difference (sourceAgent field_diff_agent)
   * into the same findings queue. Same queued/skipped shape; fires
   * `field_diff_review_complete`.
   */
  runFieldDiff: (dealId, body = {}) =>
    losHttp
      .post(`${base(dealId)}/field-diff/run`, body, cfg("AI: Field difference"))
      .then((r) => r.data),

  // ── Acquisitions-pipeline agents (correspondent loans we're BUYING) ────────

  /**
   * Closed Package Review — ASYNC, acquisitions only. Treats the executed loan
   * package (Note/Mortgage/Guaranty/HUD) as the source of truth and validates
   * the underwrite backwards against the loan as closed. If no executed package
   * is on file the run completes `blocked: true` with a reason (carried on the
   * `closed_package_review_complete` notification) — that's a "upload the
   * executed package first" prompt, NOT an error.
   *
   * Body tells the agent WHAT it's reviewing (omit both to let it auto-detect,
   * but the doc says always prefer asking):
   *   packageMode  "single_scan" — the file(s) ARE one monolithic executed-package
   *                scan; the agent splits/classifies them first.
   *                "indexed" — already split (Sourcepoint / per-doc children).
   *   documentIds  [loanDocument oid, …] — which files are in scope.
   * The run summary echoes { packageMode, documentIds, modeSource: "user"|"auto" }.
   */
  runClosedPackageReview: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/closed-package/run`,
        body,
        cfg("AI: Closed package review"),
      )
      .then((r) => r.data),

  /**
   * Pre-close Acquisition Review — ASYNC, acquisitions only. ADVISORY: checks
   * the rate lock, open conditions/exceptions, and correspondent file coverage
   * on a locked-but-not-yet-closed acquisition. Never green-lights a purchase —
   * the definitive review happens post-close via closed-package. Fires
   * `acq_preclose_review_complete`.
   */
  runAcqPrecloseReview: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/acq-preclose/run`,
        body,
        cfg("AI: Pre-close acquisition review"),
      )
      .then((r) => r.data),

  /**
   * Collateral Search — ASYNC, acquisitions only. Answers one question: is this
   * loan's collateral at Sourcepoint and imaged? Queries the Sourcepoint SFTP
   * inventory/manifests by the loan's EV/LQ id. Summary:
   *   { packageIndexed, recordedInstruments: [{type, present, imagedAt}],
   *     missing: [], source: "inventory" | "sftp_live" }
   * Distinct from Closed Package Review: this checks EXISTENCE/imaging, that one
   * reviews CONTENT. Fires `collateral_search_complete`.
   */
  runCollateralSearch: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/collateral-search/run`,
        body,
        cfg("AI: Collateral search"),
      )
      .then((r) => r.data),

  /**
   * Acquisition Review (full) — ASYNC, acquisitions only. The COMPOSED post-close
   * review: extract truth → populate (the opening agent, writing cross-collection
   * into loan_acquisitions) → document presence (derived task list +
   * missing-doc specialist; VALIDATION only, nothing is gated out) → collateral
   * (executed-package split + Sourcepoint imaging) → deep closed-package checks
   * (C1-C7; their executed-package gate is reported as a phase outcome and never
   * blocks the run) → a rollup persisted to loan_processing.acquisition_reviews.
   *
   * This is the acquisitions counterpart of the preclose /ai-review roster — it
   * runs the individual agents in order rather than replacing them, so the
   * per-agent buttons still work on their own.
   *
   * Optional body:
   *   reviewPhase  "post_close_full" (default) | "post_close_light" |
   *                "post_close_super_light" — how deep the doc-presence task list goes.
   *   force        re-extract even when the truth sweep already ran.
   * Fires `acquisition_review_complete` with { populatedFields, missingDocFindings,
   * applicableTasks, checksBlocked, phaseErrors }. There is no GET for the rollup —
   * the notification and the per-agent statuses are the read surface.
   */
  runAcquisitionReview: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/acquisition-review/run`,
        body,
        cfg("AI: Acquisition review"),
      )
      .then((r) => r.data),

  /**
   * Closing readiness review — ASYNC, BOTH pipelines (tagged `pipeline: "both"`
   * in /status). AI attribution of the deterministic readiness signal's gating
   * items as external vs internal, with an owner + next action per item; the
   * `readinessCall` is never more optimistic than the deterministic level.
   * Also auto-runs when a loan enters a closing stage (Clear To Close / Closing /
   * Closed on preclose; Cleared to Purchase / Funding / Settled on acquisitions).
   * Same queued/skipped shape; fires `closing_readiness_review_complete`
   * ({ readinessLevel, externalGating, internalGating, headline }). The stored
   * review is read back via closingReadinessApi.getAiReview. Contract:
   * closing-readiness.md §6.
   */
  runClosingReadiness: (dealId, body = {}) =>
    losHttp
      .post(
        `${base(dealId)}/closing-readiness/run`,
        body,
        cfg("AI: Closing readiness"),
      )
      .then((r) => r.data),

  /**
   * GET /admin/loans/{dealId}/ai-reviews/findings?status=… — the loan's candidate
   * findings for the AI Reviews findings queue. Omit status for all; statuses are
   * staged | promoted | rejected | waived | superseded. Returns the findings
   * array from { dealId, findings: [...], count }. Each finding:
   *   { candidateId, title, description, severity (info|warning|critical),
   *     sourceAgent, requirementType, docType, status, reasoning, evidence[],
   *     authorityToWaive, createdAt }
   */
  getFindings: (dealId, status) =>
    losHttp
      .get(`${base(dealId)}/findings`, {
        ...cfg("AI: findings"),
        params: status ? { status } : undefined,
      })
      .then((r) => r.data?.findings || []),

  /**
   * POST /admin/loans/{dealId}/ai-reviews/findings/{candidateId}/action — act on
   * one finding. Body: { action, editedTitle?, editedBody?, reason?, rejectCategory? }.
   *   promote / edit_promote → projects an LOS condition (losConditionId);
   *     hard_exception_checker findings project an EXCEPTION REQUEST instead
   *     (losExceptionId).
   *   reject → requires reason + rejectCategory.
   *   acknowledge_waive → status waived, projects nothing.
   */
  actionFinding: (dealId, candidateId, payload) =>
    losHttp
      .post(
        `${base(dealId)}/findings/${candidateId}/action`,
        payload,
        cfg("AI: finding action"),
      )
      .then((r) => r.data),

  /**
   * GET /admin/loans/{dealId}/ai-reviews/field-writes — the exact per-field diff
   * of everything the Loan Opening agent populated. Returns the rows array from
   * { dealId, fieldWrites: [...] }, newest first. Each row:
   *   { provenanceId, field, old, new, extractor, sourceDoc, actor, mode, ts,
   *     reverted, revertOf }
   * Rows with `revertOf` set are the reversal entries themselves (not agent
   * populations) — filter those out for the diff table.
   */
  getFieldWrites: (dealId) =>
    losHttp
      .get(`${base(dealId)}/field-writes`, cfg("AI: field-writes"))
      .then((r) => r.data?.fieldWrites || []),

  /**
   * POST /admin/loans/{dealId}/ai-reviews/field-writes/{provenanceId}/revert —
   * reverse ONE agent field-population (restore the pre-agent value). GUARDED:
   * returns 409 { reverted:false, reason } if the field was edited after the
   * agent wrote it (no clobbering). 200 { reverted:true, fieldPath, restored }
   * on success; the restored field is stamped manual-source so future agent runs
   * won't re-write it, and the reversal lands on the Activity Log timeline.
   */
  revertFieldWrite: (dealId, provenanceId) =>
    losHttp
      .post(
        `${base(dealId)}/field-writes/${provenanceId}/revert`,
        {},
        cfg("AI: revert field-write"),
      )
      .then((r) => r.data),

  // ── Opening Data Assistant (opening-data-assistant.md) ──────────────────────
  // Propose-then-accept data population. Unlike the Loan Opening AGENT (auto-apply,
  // run-once, gated), the ASSISTANT stages proposals for per-field review and writes
  // NOTHING to the loan until a proposal is accepted. Admin OR underwriter.
  // NB: these live at /admin/loans/{dealId}/opening-assistant — NOT under the
  // /ai-reviews base the run/findings endpoints use.

  /**
   * POST /admin/loans/{dealId}/ai-reviews/opening-assistant/propose — regenerate
   * proposals from the loan's current extracts. UNGATED: mutates nothing, does NOT
   * consume the Loan Opening run-once. Re-proposing refreshes pending rows in place
   * (accepted/rejected rows are kept as audit). Also runs the bank-statement
   * cash-liquidity suggester (result.bankLiquidity). Returns { queued/proposals,
   * byMapper/byExtractor, bankLiquidity? }.
   */
  proposeOpeningAssistant: (dealId) =>
    losHttp
      .post(
        `/admin/loans/${dealId}/opening-assistant/propose`,
        {},
        cfg("AI: opening-assistant propose"),
      )
      .then((r) => r.data),

  /**
   * GET /admin/loans/{dealId}/ai-reviews/opening-assistant?status=… — the proposal
   * list, one row per field the extracts would populate. status: pending_review
   * (default) | applied | rejected. Returns the `proposals` array from
   * { dealId, proposals: [...], count }. Each row:
   *   { queueId, fieldPath, proposedValue, currentValue, extractor, sourceDocName,
   *     sourceDocType, sourceBlob, reasoning, status, ts }
   */
  getOpeningAssistant: (dealId, status = "pending_review") =>
    losHttp
      .get(`/admin/loans/${dealId}/opening-assistant`, {
        ...cfg("AI: opening-assistant list"),
        params: status ? { status } : undefined,
      })
      .then((r) => r.data?.proposals || []),

  /**
   * Every proposal regardless of status — the backend returns all rows when the
   * status query param is present but empty (`status or None` → None). Used to
   * show per-status counts on the tab strip and filter client-side.
   */
  getOpeningAssistantAll: (dealId) =>
    losHttp
      .get(`/admin/loans/${dealId}/opening-assistant`, {
        ...cfg("AI: opening-assistant list (all)"),
        params: { status: "" },
      })
      .then((r) => r.data?.proposals || []),

  /**
   * POST /admin/loans/{dealId}/ai-reviews/opening-assistant/{queueId}/action — act
   * on one proposal. Body: { action: "apply" | "edit" | "reject", editedValue?,
   * reason?, force? }. apply/edit write the field via the HITL writeback (provenance
   * ledgered + revertable — the row shows up in field-writes). reject records the
   * decision and writes nothing. A proposal targeting a manually-set field is refused
   * unless force:true ("overwrite manual value?" confirm).
   */
  actionOpeningAssistant: (dealId, queueId, payload) =>
    losHttp
      .post(
        `/admin/loans/${dealId}/opening-assistant/${queueId}/action`,
        payload,
        cfg("AI: opening-assistant action"),
      )
      .then((r) => r.data),
};

export default losAiReviewsApi;
